import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';

export type ShMessageSeverity = 'info' | 'success' | 'warning' | 'danger';

/**
 * An inline message (a login error, a hint above a form).
 *
 *   <sh-message severity="danger" text="account.login.error.general" />
 *   <sh-message severity="info">Projected <b>content</b></sh-message>
 *
 * `text` is a translation key or plain text; content projected into the
 * message is shown after it. With `closable` the message has a close
 * button and emits `closed`.
 *
 * `highContrast` fills the message with the solid status colour instead of
 * the pastel tint, for messages that must stand out (the dashboard issues).
 * Plain inline messages keep the default.
 *
 *   <sh-message severity="danger" highContrast text="..." />
 */
@Component({
  selector: 'sh-message',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule, MessageModule],
  template: `
    <p-message
      [severity]="primeSeverity()"
      [closable]="closable()"
      [styleClass]="highContrast() ? 'message-high-contrast' : undefined"
      (onClose)="closed.emit()"
      ><ng-container *ngIf="text()">{{ text() | translate }}</ng-container
      ><ng-content></ng-content
    ></p-message>
  `,
})
export class ShMessageComponent {
  readonly severity = input<ShMessageSeverity>('info');
  /** Translation key or plain text. */
  readonly text = input<string>();
  readonly closable = input(false, { transform: booleanAttribute });
  /** Solid status colour with contrasting text instead of the pastel tint. */
  readonly highContrast = input(false, { transform: booleanAttribute });

  readonly closed = output<void>();

  protected readonly primeSeverity = computed(() => {
    const severity = this.severity();
    return severity === 'danger'
      ? 'error'
      : severity === 'warning'
      ? 'warn'
      : severity;
  });
}
